'use client'

import { Button } from '@heroui/react'
import { EmptyState as ProEmptyState } from '@heroui-pro/react'
import Link from 'next/link'
import type { ReactNode } from 'react'

import { Icon } from '@/components/ui/icon'
import type { IconName } from '@/config/icons'

type Action =
  | { href: string; label: string; onPress?: undefined }
  | { onPress: () => void; label: string; href?: undefined }

interface EmptyStateProps {
  icon: IconName
  title: string
  description: ReactNode
  primary?: Action
  secondary?: Action
  /**
   * Trims the vertical padding for pages where the empty state shares the
   * screen with other sections (e.g. Payments: saved cards + history), so an
   * empty first section doesn't push the populated one below the fold.
   */
  compact?: boolean
}

function ActionButton({
  action,
  variant,
}: {
  action: Action
  variant: 'primary' | 'ghost'
}) {
  if (action.href) {
    return variant === 'primary' ? (
      <Link href={action.href} className="inline-flex no-underline">
        <Button variant="primary">{action.label}</Button>
      </Link>
    ) : (
      <Link href={action.href} className="inline-flex no-underline">
        <Button variant="ghost">{action.label}</Button>
      </Link>
    )
  }
  return variant === 'primary' ? (
    <Button variant="primary" onPress={action.onPress}>
      {action.label}
    </Button>
  ) : (
    <Button variant="ghost" onPress={action.onPress}>
      {action.label}
    </Button>
  )
}

// Shared "nothing here yet" surface, backed by HeroUI Pro's EmptyState. Either
// action can be a Link (href) or a button handler (onPress). `primary` is
// optional so callers with a purely informational empty state (or a
// conditional CTA) can omit it.
export function EmptyState({
  icon,
  title,
  description,
  primary,
  secondary,
  compact = false,
}: EmptyStateProps) {
  return (
    <div className={`${compact ? 'py-2' : 'py-12 sm:py-16'} flex justify-center`}>
      <ProEmptyState className="max-w-md">
        <ProEmptyState.Header>
          <ProEmptyState.Media variant="icon">
            <Icon icon={icon} size="xl" />
          </ProEmptyState.Media>
          <ProEmptyState.Title>{title}</ProEmptyState.Title>
          <ProEmptyState.Description>{description}</ProEmptyState.Description>
        </ProEmptyState.Header>
        {(primary || secondary) && (
          <ProEmptyState.Content>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2">
              {primary && <ActionButton action={primary} variant="primary" />}
              {secondary && <ActionButton action={secondary} variant="ghost" />}
            </div>
          </ProEmptyState.Content>
        )}
      </ProEmptyState>
    </div>
  )
}
